import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Trash2, Check, EyeOff, Plus } from "lucide-react";
import { Stars } from "@/components/Testimonials";
import { API, auth, inputClass, labelClass, primaryBtn, ghostBtn, fmtDate, handleAuthError } from "./shared";
import { useConfirm } from "./useConfirm";

const AdminReviews = ({ token, logout }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", vehicle: "", rating: 5, comment: "" });
  const [saving, setSaving] = useState(false);
  const { confirm, dialog } = useConfirm();

  const load = () =>
    axios.get(`${API}/reviews/all`, auth(token)).then((r) => setItems(r.data)).catch((err) => handleAuthError(err, logout, () => {}));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/reviews/admin`, form, auth(token));
      toast.success("Avis publié");
      setForm({ name: "", vehicle: "", rating: 5, comment: "" });
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de l'ajout"));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/reviews/${id}`, { status }, auth(token));
      toast.success(status === "approved" ? "Avis publié" : "Avis masqué");
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de la mise à jour"));
    }
  };

  const remove = async (r) => {
    const ok = await confirm({ title: "Supprimer cet avis ?", description: `L'avis de ${r.name} sera définitivement supprimé.` });
    if (!ok) return;
    try {
      await axios.delete(`${API}/reviews/${r.id}`, auth(token));
      toast.success("Avis supprimé");
      load();
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de la suppression"));
    }
  };

  const pending = items.filter((r) => r.status === "pending");
  const approved = items.filter((r) => r.status === "approved");

  const Card = ({ r, i, prefix }) => (
    <article className={`rounded-2xl border p-6 ${r.status === "pending" ? "border-gold/40 bg-cream" : "border-brand/10 bg-white"}`} data-testid={`${prefix}-review-${i}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-display text-base font-bold text-forest">{r.name}</p>
          <p className="text-xs text-brand">{r.vehicle || "—"} · {fmtDate(r.created_at)} · {r.source === "admin" ? "Ajouté par vous" : "Envoyé par le client"}</p>
        </div>
        <Stars value={r.rating} testid={`${prefix}-review-${i}-stars`} />
      </div>
      <p className="mt-4 text-sm leading-relaxed text-forest">“{r.comment}”</p>
      <div className="mt-5 flex flex-wrap gap-2">
        {r.status === "pending" ? (
          <button onClick={() => setStatus(r.id, "approved")} data-testid={`${prefix}-review-${i}-approve`} className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white transition-colors hover:bg-brand-hover">
            <Check className="h-3.5 w-3.5" /> Publier
          </button>
        ) : (
          <button onClick={() => setStatus(r.id, "pending")} data-testid={`${prefix}-review-${i}-hide`} className={ghostBtn}>
            <EyeOff className="h-3.5 w-3.5" /> Masquer
          </button>
        )}
        <button onClick={() => remove(r)} data-testid={`${prefix}-review-${i}-delete`} className={`${ghostBtn} hover:border-gold hover:text-gold`}>
          <Trash2 className="h-3.5 w-3.5" /> Supprimer
        </button>
      </div>
    </article>
  );

  return (
    <div data-testid="admin-reviews-tab">
      {dialog}
      <form onSubmit={add} className="rounded-3xl bg-mint p-8" data-testid="admin-review-form">
        <h2 className="font-display flex items-center gap-2 text-lg font-bold text-forest">
          <Star className="h-5 w-5 text-gold" />
          Ajouter un avis client
        </h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Nom du client *</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-review-name-input" className={inputClass} placeholder="Ex : Mme Koné" />
          </div>
          <div>
            <label className={labelClass}>Véhicule</label>
            <input value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} data-testid="admin-review-vehicle-input" className={inputClass} placeholder="Ex : Toyota RAV4" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Note</label>
            <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size="h-7 w-7" testid="admin-review-stars" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Commentaire *</label>
            <textarea required rows={3} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="admin-review-comment-input" className={`${inputClass} resize-none`} />
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="admin-review-submit-button" className={`mt-6 ${primaryBtn}`}>
          <Plus className="h-4 w-4" />
          {saving ? "Enregistrement..." : "Publier l'avis"}
        </button>
      </form>

      <h2 className="font-display mt-14 text-lg font-bold text-forest">
        En attente de validation <span className="text-gold">({pending.length})</span>
      </h2>
      <div className="mt-6 grid gap-5 md:grid-cols-2">
        {pending.map((r, i) => <Card key={r.id} r={r} i={i} prefix="pending" />)}
        {pending.length === 0 && <p className="col-span-full rounded-2xl border-2 border-dashed border-brand/20 px-6 py-8 text-sm text-brand">Aucun avis en attente.</p>}
      </div>

      <h2 className="font-display mt-14 text-lg font-bold text-forest">
        Avis publiés <span className="text-gold">({approved.length})</span>
      </h2>
      <div className="mt-6 grid gap-5 md:grid-cols-2">
        {approved.map((r, i) => <Card key={r.id} r={r} i={i} prefix="approved" />)}
        {approved.length === 0 && <p className="col-span-full rounded-2xl border-2 border-dashed border-brand/20 px-6 py-8 text-sm text-brand">Aucun avis publié.</p>}
      </div>
    </div>
  );
};

export default AdminReviews;
